import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import { 
  ArrowLeft, Plus, User, Phone, Mail, Briefcase, Edit, Trash2, 
  Users, Search, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STAFF_ROLES = [
  { id: 'technician', label: 'Technician' },
  { id: 'supervisor', label: 'Supervisor' },
  { id: 'assistant', label: 'Assistant' },
  { id: 'specialist', label: 'Specialist' },
];

export default function StaffManagement() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'technician',
    skills: []
  });

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/provider/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only company providers can manage staff');
        navigate('/provider/home');
      } else {
        toast.error('Failed to load staff');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Open add/edit dialog
  const openStaffDialog = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setStaffForm({
        name: staffMember.name || '',
        email: staffMember.email || '',
        phone: staffMember.phone || '',
        role: staffMember.role || 'technician',
        skills: staffMember.skills || []
      });
    } else {
      setEditingStaff(null);
      setStaffForm({
        name: '',
        email: '',
        phone: '',
        role: 'technician',
        skills: []
      });
    }
    setShowStaffDialog(true);
  };

  // Save staff
  const handleSaveStaff = async () => {
    if (!staffForm.name.trim()) {
      toast.error('Staff name is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (editingStaff) {
        await axios.put(`${API}/provider/staff/${editingStaff.id}`, staffForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Staff updated successfully');
      } else {
        await axios.post(`${API}/provider/staff`, staffForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Staff added successfully');
      }
      setShowStaffDialog(false);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete staff
  const handleDeleteStaff = async (staffId) => {
    try {
      await axios.delete(`${API}/provider/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff removed');
      setShowDeleteConfirm(null);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    }
  };

  // Filter staff by search
  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">Staff Management</h1>
          </div>
          <Button 
            size="sm"
            onClick={() => openStaffDialog()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="add-staff-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Staff
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Stats Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{staff.length}</p>
              <p className="text-sm text-slate-500">Total Staff Members</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 bg-white"
            data-testid="staff-search"
          />
        </div>

        {/* Staff List */}
        {filteredStaff.length > 0 ? (
          <div className="space-y-3">
            {filteredStaff.map((member) => (
              <div 
                key={member.id}
                className="bg-white rounded-xl p-4 shadow-sm"
                data-testid={`staff-card-${member.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{member.name}</h3>
                      <p className="text-sm text-amber-600 capitalize">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openStaffDialog(member)}
                      className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"
                      data-testid={`edit-staff-${member.id}`}
                    >
                      <Edit className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(member)}
                      className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center"
                      data-testid={`delete-staff-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {member.phone}
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {member.email}
                    </div>
                  )}
                </div>
                
                {/* Skills */}
                {member.skills && member.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {member.skills.map((skill, idx) => (
                      <span 
                        key={`skill-${skill}`}
                        className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No staff members yet</p>
            <p className="text-sm text-slate-400">Add your team members to assign them to jobs</p>
            <Button 
              onClick={() => openStaffDialog()}
              className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Staff
            </Button>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <ProviderBottomNav />

      {/* Add/Edit Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
            </DialogTitle>
            <DialogDescription>
              {editingStaff ? 'Update staff details' : 'Add a new team member to your company'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Juan Dela Cruz"
                className="h-12 mt-1.5"
                data-testid="staff-name-input"
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select 
                value={staffForm.role} 
                onValueChange={(v) => setStaffForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className="h-12 mt-1.5" data-testid="staff-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {STAFF_ROLES.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Phone Number</Label>
              <Input
                value={staffForm.phone}
                onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="09171234567"
                className="h-12 mt-1.5"
                data-testid="staff-phone-input"
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="juan@example.com"
                className="h-12 mt-1.5"
                data-testid="staff-email-input"
              />
            </div>
            
            <Button
              onClick={handleSaveStaff}
              disabled={isSubmitting}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="save-staff-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : editingStaff ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : (
                <Plus className="w-5 h-5 mr-2" />
              )}
              {editingStaff ? 'Update Staff' : 'Add Staff'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Remove Staff Member?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{showDeleteConfirm?.name}</strong>? 
              They will no longer be available for job assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteStaff(showDeleteConfirm?.id)}
              className="flex-1 bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-staff"
            >
              Remove Staff
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
